'use client';

import type { Detection, FaceDetector } from '@mediapipe/tasks-vision';

import { getAvatarDetectionImageSource } from '@/utils/avatarImageProxy';

const MEDIAPIPE_WASM_ROOT = '/mediapipe-wasm';
const FACE_DETECTOR_MODEL_PATH =
  '/mediapipe-models/blaze_face_short_range.tflite';
const FACE_HORIZONTAL_PADDING_RATIO = 0.35;
const FACE_TOP_PADDING_RATIO = 0.35;
const FACE_BOTTOM_PADDING_RATIO = 0.75;
const AVATAR_OUTPUT_SIZE = 256;
const AVATAR_OUTPUT_QUALITY = 0.92;
const MAX_AVATAR_CROP_CACHE_ENTRIES = 50;
const MAX_AVATAR_CROP_CACHE_BYTES = 8 * 1024 * 1024;
const MEDIAPIPE_CPU_DELEGATE_INFO =
  'Created TensorFlow Lite XNNPACK delegate for CPU';

type AvatarFaceBounds = {
  imageWidth: number;
  imageHeight: number;
  originX: number;
  originY: number;
  width: number;
  height: number;
};

type AvatarCropArea = {
  originX: number;
  originY: number;
  size: number;
};

type FaceDetectionAnalysis = {
  image: HTMLImageElement;
  faceBounds: AvatarFaceBounds | null;
};

type EmscriptenModuleOverride = {
  locateFile?: (path: string, scriptDirectory?: string) => string;
  mainScriptUrlOrBlob?: string | Blob;
  printErr?: (message: string) => void;
  stderr?: (message: string) => void;
};

/**
 * Tracks generated avatar crops with bounded memory and LRU eviction.
 */
export class AvatarCropCache {
  private readonly cache = new Map<string, string | null>();
  private totalBytes = 0;

  /**
   * Creates a bounded avatar crop cache.
   *
   * @param maxEntries The maximum number of cached avatar URLs.
   * @param maxBytes The approximate maximum string memory to retain.
   */
  constructor(
    private readonly maxEntries: number = MAX_AVATAR_CROP_CACHE_ENTRIES,
    private readonly maxBytes: number = MAX_AVATAR_CROP_CACHE_BYTES
  ) {}

  /**
   * Returns the cached crop for a URL and refreshes its LRU position.
   *
   * @param key The avatar image URL cache key.
   * @returns The cached value, or `undefined` when the key is absent.
   */
  get(key: string) {
    if (!this.cache.has(key)) {
      return undefined;
    }

    const value = this.cache.get(key) ?? null;

    this.cache.delete(key);
    this.cache.set(key, value);

    return value;
  }

  /**
   * Stores a crop result and evicts the oldest entries until limits are met.
   *
   * @param key The avatar image URL cache key.
   * @param value The generated crop data URL or `null` for a cacheable miss.
   * @returns Nothing.
   */
  set(key: string, value: string | null) {
    if (this.cache.has(key)) {
      const previousValue = this.cache.get(key) ?? null;

      this.totalBytes -= this.getEntrySize(previousValue);
      this.cache.delete(key);
    }

    this.cache.set(key, value);
    this.totalBytes += this.getEntrySize(value);
    this.evictOverflow();
  }

  /**
   * Returns the current cache key order from oldest to newest.
   *
   * @returns The cache keys in eviction order.
   */
  keys() {
    return Array.from(this.cache.keys());
  }

  /**
   * Returns the current approximate cache size in bytes.
   *
   * @returns The approximate retained string memory.
   */
  getSizeBytes() {
    return this.totalBytes;
  }

  /**
   * Estimates the in-memory cost of a cached crop string.
   *
   * @param value The cached crop value.
   * @returns The approximate memory usage in bytes.
   */
  private getEntrySize(value: string | null) {
    return value ? value.length * 2 : 0;
  }

  /**
   * Removes least-recently-used entries until both cache limits are satisfied.
   *
   * @returns Nothing.
   */
  private evictOverflow() {
    while (
      this.cache.size > this.maxEntries ||
      this.totalBytes > this.maxBytes
    ) {
      const oldestKey = this.cache.keys().next().value;

      if (!oldestKey) {
        return;
      }

      const oldestValue = this.cache.get(oldestKey) ?? null;

      this.totalBytes -= this.getEntrySize(oldestValue);
      this.cache.delete(oldestKey);
    }
  }
}

const avatarCropCache = new AvatarCropCache();
let faceDetectorPromise: Promise<FaceDetector> | null = null;

/**
 * Raised when face detection cannot run because the browser/runtime plumbing
 * failed rather than because the image lacks a face.
 */
export class FaceDetectionUnavailableError extends Error {
  code: string;

  /**
   * Creates a typed face-detection infrastructure error.
   *
   * @param code Stable machine-readable reason for the failure.
   * @param message Human-readable failure detail.
   */
  constructor(code: string, message: string) {
    super(message);
    this.name = 'FaceDetectionUnavailableError';
    this.code = code;
  }
}

/**
 * Clamps a numeric value into the provided range.
 *
 * @param value The input value to clamp.
 * @param min The inclusive lower bound.
 * @param max The inclusive upper bound.
 * @returns The clamped value.
 */
function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Returns whether a MediaPipe stderr message is the noisy CPU delegate info
 * line that gets surfaced as a console error in the browser.
 *
 * @param message The stderr message emitted by the Emscripten runtime.
 * @returns `true` when the message should be suppressed.
 */
function isMutedMediapipeInfoMessage(message: string) {
  return message.includes(MEDIAPIPE_CPU_DELEGATE_INFO);
}

/**
 * Runs a MediaPipe initialization block with a temporary Emscripten stderr
 * hook that filters the known CPU delegate info message.
 *
 * @param operation The async MediaPipe initialization work to run.
 * @returns The operation result.
 */
async function withMutedMediapipeCpuDelegateInfo<T>(
  operation: () => Promise<T>
) {
  const globalScope = globalThis as typeof globalThis & {
    Module?: EmscriptenModuleOverride;
  };
  const previousModule = globalScope.Module;

  const forwardStderr = (message: string) => {
    if (isMutedMediapipeInfoMessage(message)) {
      return;
    }

    if (typeof previousModule?.stderr === 'function') {
      previousModule.stderr(message);

      return;
    }

    if (typeof previousModule?.printErr === 'function') {
      previousModule.printErr(message);

      return;
    }

    // eslint-disable-next-line no-console
    console.error(message);
  };

  globalScope.Module = {
    ...previousModule,
    printErr: forwardStderr,
    stderr: forwardStderr,
  };

  try {
    return await operation();
  } finally {
    if (previousModule) {
      globalScope.Module = previousModule;
    } else {
      delete globalScope.Module;
    }
  }
}

/**
 * Calculates the detected face area for prominence ranking.
 *
 * @param detection A MediaPipe face detection result.
 * @returns The detection area in pixels, or `0` when no bounding box exists.
 */
function getFaceArea(detection: Detection) {
  const boundingBox = detection.boundingBox;

  return boundingBox ? boundingBox.width * boundingBox.height : 0;
}

/**
 * Picks the most prominent detected face and normalizes it with the image size.
 *
 * @param detections The raw MediaPipe detections.
 * @param imageWidth The width of the source image.
 * @param imageHeight The height of the source image.
 * @returns The normalized face bounds, or `null` if no face box is available.
 */
function getPrimaryFaceBounds(
  detections: Detection[],
  imageWidth: number,
  imageHeight: number
): AvatarFaceBounds | null {
  const primaryFace = [...detections]
    .filter((detection) => !!detection.boundingBox)
    .sort((a, b) => {
      const areaDelta = getFaceArea(b) - getFaceArea(a);

      if (areaDelta !== 0) {
        return areaDelta;
      }

      const bScore = b.categories[0]?.score ?? 0;
      const aScore = a.categories[0]?.score ?? 0;

      return bScore - aScore;
    })[0];

  if (!primaryFace?.boundingBox) {
    return null;
  }

  const { originX, originY, width, height } = primaryFace.boundingBox;

  return {
    imageWidth,
    imageHeight,
    originX,
    originY,
    width,
    height,
  };
}

/**
 * Loads an image element so it can be passed into the face detector and the
 * crop renderer.
 *
 * @param sourceUrl The image URL to load.
 * @returns A loaded `HTMLImageElement`.
 */
async function loadImage(sourceUrl: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    if (typeof Image === 'undefined') {
      reject(new Error('Image constructor is not available.'));

      return;
    }

    const image = new Image();

    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error(`Avatar image could not be loaded: ${sourceUrl}`));
    image.src = sourceUrl;
  });
}

/**
 * Creates a MediaPipe face detector configured for image analysis.
 *
 * @returns A configured `FaceDetector` instance.
 */
async function createFaceDetector() {
  return withMutedMediapipeCpuDelegateInfo(async () => {
    const vision = await import('@mediapipe/tasks-vision');
    const fileset =
      await vision.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_ROOT);

    return vision.FaceDetector.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: FACE_DETECTOR_MODEL_PATH,
      },
      minDetectionConfidence: 0.5,
      runningMode: 'IMAGE',
    });
  });
}

/**
 * Returns a singleton MediaPipe face detector configured for one-off image analysis.
 *
 * @returns A ready-to-use `FaceDetector`.
 */
async function getFaceDetector() {
  if (!faceDetectorPromise) {
    faceDetectorPromise = createFaceDetector().catch((error) => {
      faceDetectorPromise = null;
      throw error;
    });
  }

  return faceDetectorPromise;
}

/**
 * Kicks off the MediaPipe face detector's WASM + model load without waiting
 * on it. Safe to call multiple times (idempotent via the singleton promise)
 * and safe to call on the server (analyzeFaceImage guards `window`
 * availability, but this warms only the detector, not analysis).
 *
 * @returns Nothing — call sites don't need the detector, just the head start.
 */
export function warmFaceDetector() {
  if (typeof window === 'undefined') {
    return;
  }

  getFaceDetector().catch(() => {
    // Swallowed: a real failure surfaces again on the actual detect call,
    // which is what callers act on.
  });
}

/**
 * Analyzes an image with the shared MediaPipe detector and returns the primary
 * face bounds when detection succeeds.
 *
 * @param imageUrl The image URL to analyze.
 * @returns The loaded image together with the primary detected face bounds.
 * @throws {FaceDetectionUnavailableError} When detector setup or image loading fails.
 */
async function analyzeFaceImage(
  imageUrl: string
): Promise<FaceDetectionAnalysis> {
  if (!imageUrl) {
    throw new FaceDetectionUnavailableError(
      'missing_image_url',
      'Face detection requires an image URL.'
    );
  }

  if (typeof window === 'undefined') {
    throw new FaceDetectionUnavailableError(
      'window_unavailable',
      'Face detection requires a browser environment.'
    );
  }

  const detectionImageSource = getAvatarDetectionImageSource(
    imageUrl,
    window.location.origin
  );

  if (!detectionImageSource) {
    throw new FaceDetectionUnavailableError(
      'unsupported_image_source',
      'Face detection could not resolve a supported image source.'
    );
  }

  let image: HTMLImageElement;

  try {
    image = await loadImage(detectionImageSource);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Unknown image loading error.';

    throw new FaceDetectionUnavailableError(
      'image_load_failed',
      `Face detection could not load the image. ${message}`
    );
  }

  const imageWidth = image.naturalWidth || image.width;
  const imageHeight = image.naturalHeight || image.height;

  if (!imageWidth || !imageHeight) {
    throw new FaceDetectionUnavailableError(
      'invalid_image_dimensions',
      'Face detection could not read the image dimensions.'
    );
  }

  let detector: FaceDetector;

  try {
    detector = await getFaceDetector();
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'Unknown face detector initialization error.';

    throw new FaceDetectionUnavailableError(
      'detector_initialization_failed',
      `Face detector initialization failed. ${message}`
    );
  }

  try {
    const detectionResult = detector.detect(image);

    return {
      image,
      faceBounds: getPrimaryFaceBounds(
        detectionResult.detections,
        imageWidth,
        imageHeight
      ),
    };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Unknown face detection error.';

    throw new FaceDetectionUnavailableError(
      'detection_failed',
      `Face detection failed while analyzing the image. ${message}`
    );
  }
}

/**
 * Expands the detected face box into a square crop suitable for an avatar.
 *
 * @param faceBounds The detected face bounds together with the full image size.
 * @returns A clamped square crop area in source-image pixels.
 */
export function getAvatarCropAreaFromFaceBounds(
  faceBounds: AvatarFaceBounds
): AvatarCropArea {
  const expandedLeft =
    faceBounds.originX - faceBounds.width * FACE_HORIZONTAL_PADDING_RATIO;
  const expandedRight =
    faceBounds.originX + faceBounds.width * (1 + FACE_HORIZONTAL_PADDING_RATIO);
  const expandedTop =
    faceBounds.originY - faceBounds.height * FACE_TOP_PADDING_RATIO;
  const expandedBottom =
    faceBounds.originY + faceBounds.height * (1 + FACE_BOTTOM_PADDING_RATIO);
  const expandedWidth = expandedRight - expandedLeft;
  const expandedHeight = expandedBottom - expandedTop;
  const size = Math.min(
    Math.max(expandedWidth, expandedHeight),
    faceBounds.imageWidth,
    faceBounds.imageHeight
  );
  const centerX = (expandedLeft + expandedRight) / 2;
  const centerY = (expandedTop + expandedBottom) / 2;
  const maxOriginX = Math.max(faceBounds.imageWidth - size, 0);
  const maxOriginY = Math.max(faceBounds.imageHeight - size, 0);

  return {
    originX: Math.round(clamp(centerX - size / 2, 0, maxOriginX)),
    originY: Math.round(clamp(centerY - size / 2, 0, maxOriginY)),
    size: Math.round(size),
  };
}

/**
 * Renders a square avatar crop into a compact data URL for display.
 *
 * @param image The loaded source image.
 * @param cropArea The square crop area to render.
 * @returns A JPEG data URL, or `null` when canvas rendering is unavailable.
 */
function renderAvatarCrop(
  image: HTMLImageElement,
  cropArea: AvatarCropArea
): string | null {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return null;
  }

  canvas.width = AVATAR_OUTPUT_SIZE;
  canvas.height = AVATAR_OUTPUT_SIZE;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    cropArea.originX,
    cropArea.originY,
    cropArea.size,
    cropArea.size,
    0,
    0,
    AVATAR_OUTPUT_SIZE,
    AVATAR_OUTPUT_SIZE
  );

  return canvas.toDataURL('image/jpeg', AVATAR_OUTPUT_QUALITY);
}

/**
 * Returns whether face detection completed successfully and found at least one face.
 *
 * @param imageUrl The image URL to analyze.
 * @returns `true` when a face was found, otherwise `false`.
 * @throws {FaceDetectionUnavailableError} When face detection could not run.
 */
export async function hasDetectedFace(imageUrl: string): Promise<boolean> {
  const analysis = await analyzeFaceImage(imageUrl);

  return analysis.faceBounds !== null;
}

/**
 * Detects the dominant face in an avatar image and returns a real square crop
 * around it. If detection fails, the caller should keep using the original image.
 *
 * @param imageUrl The avatar image URL to analyze.
 * @returns A cropped avatar data URL, or `null` when no face crop could be generated.
 */
export async function createFaceCroppedAvatar(imageUrl: string) {
  if (!imageUrl || typeof window === 'undefined') {
    return null;
  }

  const cachedCrop = avatarCropCache.get(imageUrl);

  if (cachedCrop !== undefined) {
    return cachedCrop;
  }

  try {
    const analysis = await analyzeFaceImage(imageUrl);

    if (!analysis.faceBounds) {
      avatarCropCache.set(imageUrl, null);

      return null;
    }

    const cropArea = getAvatarCropAreaFromFaceBounds(analysis.faceBounds);
    const croppedAvatar = renderAvatarCrop(analysis.image, cropArea);

    if (!croppedAvatar) {
      avatarCropCache.set(imageUrl, null);

      return null;
    }

    avatarCropCache.set(imageUrl, croppedAvatar);

    return croppedAvatar;
  } catch {
    avatarCropCache.set(imageUrl, null);

    return null;
  }
}
